// Utility helpers for sharing record links.

const shareState = {
  hasLoggedClipboardWarning: false,
};

const MOBILE_REGEX = /android|iphone|ipad|ipod|mobile/i;

const isPointerCoarse = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }

  const query = window.matchMedia('(pointer: coarse)');
  return typeof query?.matches === 'boolean' ? query.matches : false;
};

const hasTouchPoints = () =>
  typeof navigator !== 'undefined' && typeof navigator.maxTouchPoints === 'number'
    ? navigator.maxTouchPoints > 1
    : false;

const matchesMobileUA = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || navigator.vendor || '';
  return MOBILE_REGEX.test(ua);
};

export const isLikelyMobile = () => isPointerCoarse() || hasTouchPoints() || matchesMobileUA();

export const buildRecordShareUrl = (recordId, origin) => {
  const resolvedOrigin = origin ??
    (typeof window !== 'undefined' && window.location ? window.location.origin : '');

  const normalizedOrigin = resolvedOrigin ? resolvedOrigin.replace(/\/+$/, '') : '';
  const encodedId = encodeURIComponent(String(recordId));
  return `${normalizedOrigin}/record/${encodedId}`;
};

/**
 * Copy a record detail URL to the clipboard.
 * Falls back to execCommand('copy') when the async Clipboard API is unavailable.
 */
export async function copyRecordShareLink(recordId, options = {}) {
  if (!recordId) {
    throw new Error('copyRecordShareLink requires a recordId');
  }

  const shareUrl = buildRecordShareUrl(recordId, options.origin);

  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(shareUrl);
      return { success: true, url: shareUrl, method: 'clipboard' };
    } catch (error) {
      if (!shareState.hasLoggedClipboardWarning) {
        console.warn('Clipboard API copy failed, attempting fallback.', error);
        shareState.hasLoggedClipboardWarning = true;
      }
    }
  }

  if (typeof document !== 'undefined') {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      textArea.setAttribute('readonly', '');
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      textArea.style.pointerEvents = 'none';
      textArea.style.top = '-1000px';
      textArea.style.left = '-1000px';

      document.body.appendChild(textArea);
      textArea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (successful) {
        return { success: true, url: shareUrl, method: 'execCommand' };
      }
    } catch (error) {
      return { success: false, url: shareUrl, method: 'execCommand', error };
    }
  }

  return { success: false, url: shareUrl, method: 'unsupported' };
}

const shouldUseNativeShare = (options) => {
  if (options?.forceShare === true) return true;
  if (options?.forceShare === false) return false;
  return isLikelyMobile();
};

/**
 * Attempt to invoke the OS share sheet on mobile and fall back to clipboard copy on desktop.
 */
export async function shareRecordLink(recordId, options = {}) {
  if (!recordId) {
    throw new Error('shareRecordLink requires a recordId');
  }

  const shareUrl = buildRecordShareUrl(recordId, options.origin);
  const sharePayload = { url: shareUrl };

  if (options.title) {
    sharePayload.title = options.title;
  }

  if (options.text) {
    sharePayload.text = options.text;
  }

  const canUseWebShare =
    typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  if (canUseWebShare && shouldUseNativeShare(options)) {
    try {
      await navigator.share(sharePayload);
      return { success: true, url: shareUrl, action: 'shared', method: 'web-share' };
    } catch (error) {
      // Abort errors occur when the user closes the share sheet; treat as benign.
      if (error?.name === 'AbortError') {
        return {
          success: false,
          url: shareUrl,
          action: 'aborted',
          method: 'web-share',
          error,
        };
      }

      console.warn('Native share failed, falling back to clipboard.', error);
      // Continue to clipboard fallback.
    }
  }

  const copyResult = await copyRecordShareLink(recordId, options);

  return {
    success: copyResult.success,
    url: copyResult.url,
    action: copyResult.success ? 'copied' : 'unsupported',
    method: copyResult.method,
    error: copyResult.error,
  };
}
